/**
 * Keep the panel on the part of the screen the client can see.
 *
 * On a phone the panel is position:fixed at top:0 with height:100dvh. iOS 26
 * Safari breaks that in two ways: after the on-screen keyboard goes away,
 * `visualViewport.offsetTop` stays above zero, so every fixed element is DRAWN
 * lower than it is HIT-TESTED until the page is reloaded (Apple forum threads
 * 800125 / 800154; Mastodon #36144); and 100dvh no longer matches the visible
 * height under the floating tab bar. The contact step is where the keyboard
 * is used, so the deposit step right after it — X, Cancel, change the time —
 * stopped responding to taps on iPhone (Tim, 2026-10-08/09).
 *
 * The fix is to stop trusting the layout viewport: the panel's top and height
 * are set from `visualViewport` and re-set on every resize / scroll of it, and
 * a stuck offset with the keyboard closed is nudged back to zero.
 */

let panel: HTMLElement | null = null;
let raf = 0;

function vv(): VisualViewport | null {
  return typeof window !== 'undefined' ? window.visualViewport || null : null;
}

function apply(): void {
  raf = 0;
  const v = vv();
  if (!panel || !v) return;
  const keyboardOpen = window.innerHeight - v.height > 100;
  if (!keyboardOpen && v.offsetTop > 0 && window.scrollY === 0) {
    // iOS 26: the visual viewport is left scrolled after the keyboard closes
    // although the document itself is at 0 — a nudge brings it back.
    try { window.scrollTo(0, 1); window.scrollTo(0, 0); } catch { /* ignore */ }
  }
  panel.style.top = `${Math.max(0, Math.round(v.offsetTop))}px`;
  panel.style.height = `${Math.round(v.height)}px`;
}

function schedule(): void {
  if (raf) return;
  raf = 1;                       // set BEFORE the request: a frame that runs at once must not leave it stuck
  requestAnimationFrame(apply);
}

/** Start following the visual viewport (phones only; the hosted page's in-flow panel never). */
export function fitPanelToViewport(el: HTMLElement): void {
  const v = vv();
  if (!v || panel === el) return;
  if (panel) stopFittingPanel();
  panel = el;
  v.addEventListener('resize', schedule);
  v.addEventListener('scroll', schedule);
  window.addEventListener('orientationchange', schedule);
  apply();
}

export function stopFittingPanel(): void {
  const v = vv();
  if (v) { v.removeEventListener('resize', schedule); v.removeEventListener('scroll', schedule); }
  window.removeEventListener('orientationchange', schedule);
  raf = 0;
  if (panel) { panel.style.top = ''; panel.style.height = ''; }
  panel = null;
}

export function isFittingPanel(): boolean { return panel !== null; }
